import { ReactNode, createContext, FC, useEffect, useState } from 'react';

import { ApolloError, gql, useQuery } from '@apollo/client';
import { ApiPortalOrg } from '../components/portals/ApiPortalTypes';
import { useParams } from 'react-router';

interface ApiPortalOrgQueryResult {
  org: ApiPortalOrg;
  loading: boolean;
  error?: ApolloError;
  formattedOrgName: string;
}

type Props = {
  children: ReactNode;
};

/**
 * ApiPortalOrgContext gives us a top level API Portal org we can use to pass org
 * data between components on the API Portal org page.
 */
export const ApiPortalContext = createContext<ApiPortalOrgQueryResult>(
  {} as ApiPortalOrgQueryResult,
);

const GET_API_PORTAL_ORG = gql`
  query GetApiPortalOrg($filter: ApiPortalOrgsFilter) {
    devhub {
      apiPortal {
        apiPortalOrgsPaged(filter: $filter) {
          totalCount
          orgs {
            name
            _normalizedName
            categoriesPaged {
              categories {
                name
                categoryId
                description
                logo
                apiSpecsPaged {
                  totalCount
                  specs {
                    name
                    title
                    definitionPacked
                  }
                }
                apiEventsPaged {
                  totalCount
                  events {
                    id
                    title
                    summary
                    reference
                  }
                }
                apiScenariosPaged {
                  totalCount
                  scenarios {
                    id
                    title
                    summary
                    reference
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

/**
 * ApiPortalOrgProvider sets up the connection to the graph service. It allows us to use the graph client where needed in the API Portal org.
 * @param props Props of the context
 * @param props.children - The children of this context.
 */
export const ApiPortalProvider: FC<Props> = (props: Props) => {
  const { orgName } = useParams<{ orgName: string }>();
  const [org, setOrg] = useState<ApiPortalOrg>({} as ApiPortalOrg);
  const [formattedOrgName, setFormattedOrgName] = useState<string>('');

  const { loading, error, data } = useQuery(GET_API_PORTAL_ORG, {
    errorPolicy: 'all',
    variables: {
      filter: {
        name: orgName,
      },
    },
  });

  useEffect(() => {
    if (data) {
      const fetchedOrg = data?.devhub?.apiPortal?.apiPortalOrgsPaged
        ?.orgs[0] as ApiPortalOrg;
      if (fetchedOrg) {
        setOrg(fetchedOrg);
        setFormattedOrgName(
          fetchedOrg.name
            ?.split('-')
            .map(name => name.charAt(0).toUpperCase() + name.slice(1))
            .join(' '),
        );
      }
    }
  }, [data]);

  return (
    <ApiPortalContext.Provider
      value={{
        org,
        loading,
        error,
        formattedOrgName,
      }}
    >
      {props.children}
    </ApiPortalContext.Provider>
  );
};

export default ApiPortalProvider;
